import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  TaskCheckbox,
  TaskDialog,
  TaskDialogActions,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const today = new Date().toISOString().split("T")[0];

const areaOptions = [
  "Living Room",
  "Kitchen",
  "Bedroom(s)",
  "Bathroom(s)",
  "Garage",
  "Basement / Storage Room",
  "Outdoor Shed / Yard",
];

const emptyForm = {
  company_name: "",
  date: "",
  time: "",
  contact_number: "",
  areas: [],
  estimate_hours: "",
  notes: "",
};

const BookDeclutterForm = ({ projectId, onClose, onSave }) => {
  const [existingRecord, setExistingRecord] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!projectId) return;

      setErrorMessage("");
      const { data, error } = await taskDetailApi.list("book-declutter", {
        project_id: projectId,
        limit: 1,
      });

      if (error) {
        setErrorMessage(error.message || "Failed to load decluttering booking");
        return;
      }

      if (data?.[0]) {
        const row = data[0];
        setExistingRecord(row);
        setFormData({
          company_name: row.company_name || "",
          date: row.date || "",
          time: row.time || "",
          contact_number: row.contact_number || "",
          areas: row.areas || [],
          estimate_hours: String(row.estimate_hours || ""),
          notes: row.notes || "",
        });
      } else {
        setExistingRecord(null);
        setFormData(emptyForm);
      }
    };

    fetchBooking();
  }, [projectId]);

  const handleChange = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleAreaChange = (area) => {
    setFormData((previous) => {
      const updated = previous.areas.includes(area)
        ? previous.areas.filter((item) => item !== area)
        : [...previous.areas, area];
      return { ...previous, areas: updated };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!formData.company_name.trim()) {
      setErrorMessage("Company name is required");
      return;
    }
    if (!formData.date) {
      setErrorMessage("Booking date is required");
      return;
    }
    if (!formData.time) {
      setErrorMessage("Booking time is required");
      return;
    }

    setSaving(true);
    const saveData = {
      ...formData,
      estimate_hours: formData.estimate_hours
        ? parseInt(formData.estimate_hours, 10)
        : null,
      areas: formData.areas || [],
    };

    let error;
    if (existingRecord?.id) {
      ({ error } = await taskDetailApi.update(
        "book-declutter",
        existingRecord.id,
        saveData,
      ));
    } else {
      ({ error } = await taskDetailApi.create("book-declutter", {
        project_id: projectId,
        ...saveData,
      }));
    }

    setSaving(false);
    if (error) {
      setErrorMessage(error.message || "Failed to save decluttering booking");
      return;
    }

    setSuccessMessage("Decluttering booking details saved successfully.");
  };

  const handleSuccessClose = () => {
    onSave?.();
    onClose?.();
  };

  return (
    <TaskDialog
      title="Decluttering Details"
      description="Booking provider, time, areas included, and notes."
      onClose={onClose}
      className="max-w-2xl"
    >
      <form className="space-y-5" onSubmit={handleSave}>
        <TaskMessage>{errorMessage}</TaskMessage>
        <TaskMessage tone="success">{successMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="declutter-company-name"
            label="Company Name"
            value={formData.company_name}
            onChange={(event) =>
              handleChange("company_name", event.target.value)
            }
            required
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="declutter-date"
            label="Booking Date"
            type="date"
            value={formData.date}
            onChange={(event) => handleChange("date", event.target.value)}
            min={today}
            required
          />
          <TaskTextInput
            id="declutter-time"
            label="Booking Time"
            type="time"
            value={formData.time}
            onChange={(event) => handleChange("time", event.target.value)}
            required
          />
          <TaskTextInput
            id="declutter-contact-number"
            label="Contact Number"
            value={formData.contact_number}
            onChange={(event) =>
              handleChange(
                "contact_number",
                event.target.value.replace(/\D/g, ""),
              )
            }
            placeholder="+61..."
          />
          <TaskTextInput
            id="declutter-estimate-hours"
            label="Estimated Hours"
            type="number"
            min="0"
            value={formData.estimate_hours}
            onChange={(event) =>
              handleChange("estimate_hours", event.target.value)
            }
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Areas Included</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {areaOptions.map((area) => (
              <TaskCheckbox
                key={area}
                label={area}
                checked={formData.areas.includes(area)}
                onCheckedChange={() => handleAreaChange(area)}
              />
            ))}
          </div>
        </div>

        <TaskTextArea
          id="declutter-notes"
          label="Notes"
          value={formData.notes}
          onChange={(event) => handleChange("notes", event.target.value)}
          rows={4}
        />

        <TaskDialogActions
          onCancel={successMessage ? handleSuccessClose : onClose}
          submitType="submit"
          disabled={saving}
          submitLabel={saving ? "Saving..." : "Save"}
        />
      </form>
    </TaskDialog>
  );
};

export default BookDeclutterForm;
